<template>
  <div>
    <div :id="popoverId" class="ppc-display">
      <span class="ppc-display__value">{{ displayCost }}</span>
      <b-button size="sm" variant="outline-secondary" @click="openEditor" title="Editar precios">
        <b-icon icon="pencil" scale="0.85"></b-icon>
      </b-button>
    </div>

    <b-popover :target="popoverId"
               triggers="hover focus"
               placement="auto"
               boundary="viewport"
               custom-class="detail-price-popover"
               @show="onTooltipShow">
      <template #title>
        <div class="detail-price-popover__title">
          <span>Desglose de precio</span>
          <small>{{ productName }}</small>
        </div>
      </template>
      <div class="detail-price-popover__body">
        <div v-if="snapshot.loading" class="detail-price-loading">
          <b-spinner small></b-spinner>
          <span>Cargando precios...</span>
        </div>
        <template v-else>
          <div class="detail-price-popover__section detail-price-popover__section--purchase">Compra</div>
          <div v-if="snapshot.supplierNotLinked" class="detail-price-non-default">
            <b-icon icon="exclamation-triangle" class="detail-price-non-default__icon"></b-icon>
            <span>Este proveedor no tiene precio de costo para este producto</span>
          </div>
          <template v-else>
            <div v-if="snapshot.supplierIsDefault === false" class="detail-price-non-default">
              <b-icon icon="star" class="detail-price-non-default__icon"></b-icon>
              <span>No es el proveedor principal de este producto</span>
            </div>
            <div class="detail-price-row" v-for="row in snapshot.purchaseRows" :key="'purchase-' + row.key">
              <span class="detail-price-row__label">{{ row.label }}</span>
              <span class="detail-price-row__adjust" :class="row.valueClass">{{ row.value }}</span>
              <strong class="detail-price-row__amount">{{ row.running }}</strong>
            </div>
            <div class="detail-price-total">
              <template v-if="priceWithIva && snapshot.purchaseTotalIva">
                <div>
                  <div style="font-weight:500;opacity:0.9;">Costo c/IVA</div>
                  <div style="font-size:10px;opacity:0.65;">s/IVA {{ snapshot.purchaseTotal }}</div>
                </div>
                <strong>{{ snapshot.purchaseTotalIva }}</strong>
              </template>
              <template v-else>
                <span>Costo final</span>
                <strong>{{ snapshot.purchaseTotal }}</strong>
              </template>
            </div>
          </template>

          <div class="detail-price-popover__section detail-price-popover__section--sales">Venta</div>
          <div v-if="!snapshot.saleRows.length" class="detail-price-empty">
            Sin listas de venta para este producto.
          </div>
          <div v-else class="detail-price-sales">
            <div v-for="price in snapshot.saleRows" :key="'sale-' + price.key"
                 class="detail-price-sale-card"
                 :class="{'detail-price-sale-card--expandable': price.hasTax}"
                 @click.stop="price.hasTax && toggleExpand(price.key)">
              <div class="detail-price-sale-card__header">
                <span>{{ price.label }}</span>
                <strong>{{ price.final }}</strong>
              </div>
              <div class="detail-price-sale-card__chips" v-if="price.utilityLabel || price.discountLabel || price.hasTax">
                <span class="detail-price-chip detail-price-chip--utility" v-if="price.utilityLabel">{{ price.utilityLabel }}</span>
                <span class="detail-price-chip detail-price-chip--discount" v-if="price.discountLabel">{{ price.discountLabel }}</span>
                <span v-if="price.hasTax" class="detail-price-chip detail-price-chip--iva-toggle">
                  Desglose <b-icon :icon="expanded[price.key] ? 'chevron-up' : 'chevron-down'" scale="0.75" class="ml-1"></b-icon>
                </span>
              </div>
              <div class="detail-price-sale-card__tax" v-if="price.hasTax && expanded[price.key]">
                <span>Sin IVA <strong>{{ price.net }}</strong></span>
                <span>IVA {{ price.ivaLabel }} <strong>{{ price.ivaAmount }}</strong></span>
              </div>
            </div>
          </div>
        </template>
      </div>
    </b-popover>

    <product-pricing-editor ref="editor"
                            :currency="currency"
                            :edit-source="editSource"
                            @cost-saved="onEditorCostSaved"
                            @price-updated="onEditorPriceUpdated"
                            @editor-closed="onEditorClosed" />
  </div>
</template>

<script>
  import costsServiceAPI from './services'
  import ErrorHandler from '@/handler/error'
  import ProductPricingEditor from './productPricingEditor'
  import { roundMoney, netPriceFromUtility, grossFromNet, effectiveUtility, isUsingDefaultUtility } from './pricingMath'

  // Cache compartido entre todas las instancias del componente
  export const pricingCache = {}

  export default {
    name: 'ProductPriceCell',
    components: { ProductPricingEditor },
    props: {
      item: { type: Object, required: true },
      supplierId: { type: Number, default: 0 },
      currency: { type: String, default: 'ARS' },
      editSource: { type: String, default: 'purchaseRemits' },
    },
    data() {
      return {
        expanded: {},
        priceWithIva: false,
      }
    },
    mounted() {
      this.loadPriceWithIvaPreference()
      if (this.priceWithIva) {
        this.hydrate()
      }
    },
    computed: {
      popoverId() {
        return 'ppc-' + this._uid
      },
      productName() {
        return this.item && this.item.product && this.item.product.name
          ? this.item.product.name
          : 'Producto'
      },
      displayCost() {
        var cost = parseFloat((this.item && this.item.price_cost) || 0)
        if (this.priceWithIva) {
          var iva = this.getIvaAliquot()
          return this.fmt(cost * (1 + iva / 100))
        }
        return this.fmt(cost)
      },
      snapshot() {
        var hydrated = !!(this.item && this.item.pricingHydrated)
        var loading = !hydrated || !!(this.item && this.item.pricingLoading)
        var pp = hydrated ? this.getProductPurchase() : null
        var supplierNotLinked = hydrated && !pp && !!this.supplierId
        var supplierIsDefault = pp ? !!pp.default : null
        var finalCost = this.getFinalCost()
        var iva = this.getIvaAliquot()
        return {
          loading,
          supplierIsDefault,
          supplierNotLinked,
          purchaseRows: hydrated ? this.buildPurchaseRows() : [],
          purchaseTotal: this.fmt(finalCost),
          purchaseTotalIva: (hydrated && iva > 0) ? this.fmt(finalCost * (1 + iva / 100)) : null,
          saleRows: hydrated ? this.buildSaleRows() : [],
        }
      },
    },
    methods: {
      // ── Preferencia IVA ────────────────────────────────────────
      loadPriceWithIvaPreference() {
        try {
          var saved = localStorage.getItem('bcsp_prefs')
          if (!saved) return
          var parsed = JSON.parse(saved)
          if (parsed && typeof parsed.priceWithIva === 'boolean') {
            this.priceWithIva = parsed.priceWithIva
          }
        } catch (e) { /* localStorage no disponible */ }
      },
      onEditorClosed() {
        this.loadPriceWithIvaPreference()
        this.$emit('editor-closed')
      },

      // ── Tooltip ────────────────────────────────────────────────
      onTooltipShow() {
        this.expanded = {}
        this.loadPriceWithIvaPreference()
        var product = this.item.selectProduct
          || (this.item.product && this.item.product.source === 'product' ? this.item.product : null)
        var productId = product ? product.id : 0
        if(productId) delete pricingCache[productId + ':' + this.supplierId]
        this.hydrate()
      },
      toggleExpand(key) {
        this.$set(this.expanded, key, !this.expanded[key])
      },

      // ── Editor ─────────────────────────────────────────────────
      openEditor() {
        var product = this.item.selectProduct
          || (this.item.product && this.item.product.source === 'product' ? this.item.product : null)
        var productId = product ? product.id : 0
        if(!productId) return
        this.$refs.editor.open({ productId, supplierId: this.supplierId })
      },
      onEditorCostSaved({ priceCost, supplierProduct, prices, productId }) {
        this.item.price_cost = priceCost
        this.$set(this.item, '_supplierNotLinked', false)
        var sp = this.item.selectProduct
        if(sp) {
          if(!sp.product_purchase && supplierProduct) {
            this.$set(sp, 'product_purchase', Object.assign({}, supplierProduct, { price_cost: priceCost }))
          } else if(sp.product_purchase) {
            sp.product_purchase.price_cost = priceCost
            if(supplierProduct) sp.product_purchase.price_list = supplierProduct.price_list || priceCost
          }
          if(Array.isArray(prices)) {
            this.$set(sp, 'prices', prices)
          }
        }
        var pid = (sp && sp.id) || (this.item.product ? this.item.product.id : 0) || productId
        this.updateCache(pid, supplierProduct, prices)
        this.$emit('cost-saved', { priceCost, supplierProduct, prices, productId: pid })
      },
      onEditorPriceUpdated(prices) {
        if(this.item.selectProduct) {
          this.$set(this.item.selectProduct, 'prices', prices || [])
        }
        var pid = (this.item.selectProduct && this.item.selectProduct.id)
          || (this.item.product ? this.item.product.id : 0)
        this.updateCache(pid, null, prices)
        this.$emit('price-updated', prices)
      },

      // ── Hidratación ────────────────────────────────────────────
      hydrate() {
        var product = this.item.selectProduct
          || (this.item.product && this.item.product.source === 'product' ? this.item.product : null)
        var productId = product ? product.id : 0
        if(!productId) return Promise.resolve(null)

        var cacheKey = productId + ':' + this.supplierId
        if(pricingCache[cacheKey]) {
          this.applyPayload(this.item, pricingCache[cacheKey])
          return Promise.resolve(pricingCache[cacheKey])
        }

        this.$set(this.item, 'pricingLoading', true)
        return costsServiceAPI.cargarProductoCostosPorId({ product_id: productId })
          .then(response => {
            var payload = response.data || {}
            pricingCache[cacheKey] = payload
            this.applyPayload(this.item, payload)
            this.$set(this.item, 'pricingLoading', false)
            return payload
          })
          .catch(error => {
            this.$set(this.item, 'pricingLoading', false)
            this.$awn && this.$awn.alert(ErrorHandler.showError(error))
            return null
          })
      },
      applyPayload(item, payload) {
        if(!item || !payload || !payload.product) return
        var suppliersProducts = Array.isArray(payload.suppliers_products) ? payload.suppliers_products : []
        var sp = this.supplierId
          ? suppliersProducts.find(r => r.suppliers_id == this.supplierId)
          : suppliersProducts.find(r => r.default) || suppliersProducts[0] || null

        if(!item.selectProduct) {
          this.$set(item, 'selectProduct',
            item.product && item.product.source === 'product' ? item.product : {})
        }

        var productPayload = payload.product
        if(productPayload.prices) this.$set(item.selectProduct, 'prices', productPayload.prices)
        if(productPayload.erp_iva_condition_id) this.$set(item.selectProduct, 'erp_iva_condition_id', productPayload.erp_iva_condition_id)
        if(productPayload.iva_condition) this.$set(item.selectProduct, 'iva_condition', productPayload.iva_condition)

        if(sp) {
          this.$set(item.selectProduct, 'product_purchase', sp)
          var cost = this.toNum(sp.price_cost || sp.price_list)
          if(cost) this.$set(item, 'price_cost', cost)
        } else {
          this.$set(item.selectProduct, 'product_purchase', null)
        }

        this.$set(item, 'pricingHydrated', true)
      },
      updateCache(productId, supplierProduct, prices) {
        if(!productId) return
        var cacheKey = productId + ':' + this.supplierId
        var cached = pricingCache[cacheKey]
        if(!cached) return
        if(cached.product && Array.isArray(prices)) {
          this.$set(cached.product, 'prices', prices)
        }
        if(supplierProduct && Array.isArray(cached.suppliers_products)) {
          var idx = cached.suppliers_products.findIndex(r => r.id == supplierProduct.id)
          if(idx >= 0) {
            this.$set(cached.suppliers_products, idx, Object.assign({}, cached.suppliers_products[idx], supplierProduct))
          } else {
            cached.suppliers_products.push(Object.assign({}, supplierProduct))
          }
        }
      },

      // ── Snapshot helpers ───────────────────────────────────────
      getProductPurchase() {
        return this.item && this.item.selectProduct ? this.item.selectProduct.product_purchase : null
      },
      getFinalCost() {
        var pp = this.getProductPurchase()
        return parseFloat((this.item && this.item.price_cost) || (pp && pp.price_cost) || 0)
      },
      buildPurchaseRows() {
        var pp = this.getProductPurchase()
        var rows = []
        var running = pp
          ? this.toNum(pp.price_list || pp.price_cost || this.item.price_cost)
          : this.toNum(this.item.price_cost)

        rows.push({ key: 'list', label: pp ? 'Precio de lista proveedor' : 'Costo del item',
                    value: '', valueClass: '', running: this.fmt(running) })
        if(!pp) return rows

        const addMoney = (key, label, value) => {
          var amount = this.toNum(value)
          if(amount <= 0) return
          running += amount
          rows.push({ key, label, value: '+ ' + this.fmt(amount), valueClass: 'text-success', running: this.fmt(running) })
        }
        const addPercent = (key, label, value, positive) => {
          var percent = this.toNum(value)
          if(percent <= 0) return
          running = positive ? running * (1 + percent / 100) : running * (1 - percent / 100)
          rows.push({ key, label, value: (positive ? '+ ' : '- ') + percent + '%',
                      valueClass: positive ? 'text-success' : 'text-danger', running: this.fmt(running) })
        }

        addMoney('additional',        'Extra fijo inicial', pp.additional)
        addPercent('discount-1',      'Descuento 1',        pp.percentage_discount,    false)
        addPercent('discount-2',      'Descuento 2',        pp.percentage_discount_1,  false)
        addPercent('discount-3',      'Descuento 3',        pp.percentage_discount_2,  false)
        addPercent('additional-p-1',  'Adicional 1',        pp.additional_percentage,  true)
        addPercent('additional-p-2',  'Adicional 2',        pp.additional_percentage_1,true)
        addMoney('additional-final',  'Extra fijo final',   pp.additional_1)

        return rows
      },
      buildSaleRows() {
        var prices = this.item && this.item.selectProduct && Array.isArray(this.item.selectProduct.prices)
          ? this.item.selectProduct.prices : []
        var iva = this.getIvaAliquot()
        var currentCost = this.getFinalCost()

        return prices.map((price, index) => {
          var ownUtility = this.toNum(price.utility)
          var list = price.list || price.List || {}
          var listDefaultUtility = this.toNum(price.default_utility || list.utility)
          var utility = effectiveUtility({ utility: ownUtility, defaultUtility: listDefaultUtility })
          var usingDefaultUtility = isUsingDefaultUtility({ utility: ownUtility, defaultUtility: listDefaultUtility })
          var desc = this.toNum(price.desc)

          var amount, amountTotal
          if (currentCost > 0) {
            var saleNoIva = netPriceFromUtility({ cost: currentCost, utility, discount: desc })
            amount = roundMoney(saleNoIva)
            amountTotal = roundMoney(grossFromNet(saleNoIva, iva))
          } else {
            amount = this.toNum(price.amount || price.amount_total)
            amountTotal = this.toNum(price.amount_total_iva || price.amount_total || price.amount)
          }

          var ivaActual = amount && amountTotal && amountTotal > amount
            ? (iva || this.inferIva(amount, amountTotal)) : 0

          return {
            key: price.key || price.price_list_id || price.id || index,
            label: price.label || price.name || list.name || list.reference || ('Lista ' + (price.price_list_id || index + 1)),
            utilityLabel: utility ? 'Util. ' + utility + '%' + (usingDefaultUtility ? ' (pred.)' : '') : '',
            discountLabel: desc ? 'Dto. ' + desc + '%' : '',
            net: this.fmt(amount || amountTotal),
            final: this.fmt(amountTotal || amount),
            ivaAmount: this.fmt(Math.max(amountTotal - amount, 0)),
            ivaLabel: ivaActual ? ivaActual + '%' : '',
            hasTax: !!(amount && amountTotal && Math.abs(amountTotal - amount) > 0.01),
            amount,
            amount_total: amountTotal || amount,
          }
        })
      },
      getIvaAliquot() {
        var ivaFromSelect = this.item && this.item.selectProduct && this.item.selectProduct.iva_condition
        if (ivaFromSelect) return this.toNum(ivaFromSelect.aliquot || ivaFromSelect.percentage)
        var ivaFromProduct = this.item && this.item.product && this.item.product.iva_condition
        if (ivaFromProduct) return this.toNum(ivaFromProduct.aliquot || ivaFromProduct.percentage)
        return 0
      },
      inferIva(amount, amountTotal) {
        if(!amount || !amountTotal || amountTotal <= amount) return 0
        return Math.round(((amountTotal / amount) - 1) * 10000) / 100
      },

      // ── Utilities ──────────────────────────────────────────────
      toNum(value) {
        var n = parseFloat(value)
        return isNaN(n) ? 0 : n
      },
      fmt(value) {
        return Intl.NumberFormat('es-AR', { style: 'currency', currency: this.currency })
          .format(parseFloat(value || 0))
      },
    },
  }
</script>

<style scoped>
  /* ── Celda de precio ─────────────────────────────────────── */
  .ppc-display {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    padding: 3px 8px;
    background: #f8f9fa;
    border: 1px solid #ced4da;
    border-radius: 4px;
    min-height: calc(1.5em + 0.5rem + 2px);
  }
  .ppc-display__value {
    font-weight: 600;
    font-size: 13px;
  }
  /* ── Popover container ───────────────────────────────────── */
  .detail-price-popover {
    max-width: 340px;
    width: 340px;
    z-index: 2000;
    font-family: inherit;
    border: 1px solid #d1d9e0;
    border-radius: 8px;
    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.08), 0 10px 25px -5px rgba(0,0,0,0.12);
    overflow: hidden;
  }
  .detail-price-popover .popover-header {
    background: #f8fafc;
    border-bottom: 1px solid #e8edf2;
    padding: 10px 14px;
  }
  .detail-price-popover .popover-body {
    background: #fff;
    padding: 12px 14px;
  }
  /* ── Header ─────────────────────────────────────────────── */
  .detail-price-popover__title {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }
  .detail-price-popover__title > span {
    font-size: 12px;
    font-weight: 700;
    color: #1e2a38;
    letter-spacing: 0.01em;
  }
  .detail-price-popover__title small {
    font-size: 11px;
    font-weight: 400;
    color: #8a95a2;
  }
  /* ── Body ────────────────────────────────────────────────── */
  .detail-price-popover__body {
    display: flex;
    flex-direction: column;
    gap: 10px;
    font-size: 12px;
  }
  /* ── Section labels ──────────────────────────────────────── */
  .detail-price-popover__section {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding: 3px 7px;
    border-radius: 99px;
    width: fit-content;
  }
  .detail-price-popover__section--purchase {
    color: #1d4ed8;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
  }
  .detail-price-popover__section--sales {
    color: #15803d;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
  }
  /* ── Non-default supplier ────────────────────────────────── */
  .detail-price-non-default {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    color: #92400e;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 5px;
    padding: 5px 8px;
  }
  .detail-price-non-default__icon {
    flex-shrink: 0;
    font-size: 11px;
  }
  /* ── Purchase breakdown rows ─────────────────────────────── */
  .detail-price-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 52px 80px;
    gap: 6px;
    align-items: baseline;
  }
  .detail-price-row__label {
    color: #5c6b7a;
    font-size: 11.5px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .detail-price-row__adjust {
    font-size: 11px;
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
  .detail-price-row__amount {
    font-size: 11.5px;
    font-weight: 600;
    color: #1e2a38;
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
  /* ── Final cost ──────────────────────────────────────────── */
  .detail-price-total {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding: 7px 10px;
    background: #1d4ed8;
    border-radius: 5px;
    color: #fff;
    font-size: 12px;
  }
  .detail-price-total span {
    font-weight: 500;
    opacity: 0.85;
  }
  .detail-price-total strong {
    color: #fff;
    font-size: 13px;
    font-variant-numeric: tabular-nums;
  }
  /* ── Sale price cards ────────────────────────────────────── */
  .detail-price-sales {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
  .detail-price-sale-card {
    border: 1px solid #e5eaef;
    border-radius: 6px;
    padding: 7px 10px;
    background: #fff;
    transition: border-color 0.15s;
  }
  .detail-price-sale-card--expandable {
    cursor: pointer;
  }
  .detail-price-sale-card:hover {
    border-color: #b8d9b5;
  }
  .detail-price-sale-card__header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 8px;
  }
  .detail-price-sale-card__header span {
    font-size: 12px;
    font-weight: 600;
    color: #374151;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .detail-price-sale-card__header strong {
    font-size: 13px;
    color: #15803d;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
  .detail-price-sale-card__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 5px;
  }
  .detail-price-chip {
    display: inline-flex;
    align-items: center;
    padding: 1px 6px;
    border-radius: 99px;
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.01em;
  }
  .detail-price-chip--utility {
    color: #1d4ed8;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
  }
  .detail-price-chip--discount {
    color: #b91c1c;
    background: #fef2f2;
    border: 1px solid #fecaca;
  }
  .detail-price-chip--iva-toggle {
    color: #6b7280;
    background: #f3f4f6;
    border: 1px solid #d1d5db;
  }
  .detail-price-sale-card__tax {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 5px;
    margin-top: 6px;
  }
  .detail-price-sale-card__tax span {
    display: flex;
    flex-direction: column;
    gap: 1px;
    padding: 4px 7px;
    background: #fefce8;
    border: 1px solid #fef08a;
    border-radius: 4px;
    font-size: 10.5px;
    color: #78350f;
  }
  .detail-price-sale-card__tax strong {
    color: #92400e;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
  }
  /* ── Loading / empty ─────────────────────────────────────── */
  .detail-price-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px 0;
    font-size: 12px;
    color: #8a95a2;
  }
  .detail-price-empty {
    font-size: 11.5px;
    color: #8a95a2;
    text-align: center;
    padding: 10px 0 4px;
  }
  @media (max-width: 575.98px) {
    .detail-price-popover {
      max-width: 300px;
      width: 300px;
    }
  }
</style>
